import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";
import FocusTrap from "@/components/common/FocusTrap";

// This imperative confirm is the global top layer: it always pops over
// whatever opened it (often another modal). The base modal shell is z-index
// 9999 and a handful of stacked modals reach 99999, so this MUST sit above
// all of them — otherwise confirm() called from inside any modal renders
// hidden behind it (caught 2026-06-02: staff delete confirm trapped under
// the staff detail modal).
const _MODAL_STYLE_Z_TOP = { zIndex: 100000 };

// Imperative confirmation dialog — replaces native window.confirm() across the
// app. Mounted once at the root via <ConfirmProvider>; consumers call
// `const confirm = useConfirm(); const ok = await confirm({ message });`.
//
// Why imperative: most existing call sites live inside event handlers and
// react-query mutation callbacks. A declarative modal would require lifting
// state to every component (huge diff for ~30 confirm() sites). The
// imperative wrapper keeps the migration to one-line changes per call.

type ConfirmOptions = {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  // "danger" surfaces a red confirm button + warning icon (destructive ops).
  // "default" is a neutral blue confirm button (reschedule/dismiss/etc.).
  tone?: "danger" | "default";
};

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error("useConfirm must be used inside <ConfirmProvider>");
  }
  return ctx;
}

type DialogState = (ConfirmOptions & { id: number }) | null;

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const [state, setState] = useState<DialogState>(null);
  const resolverRef = useRef<((v: boolean) => void) | null>(null);
  const idRef = useRef(0);

  const confirm = useCallback<ConfirmFn>((opts) => {
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
      idRef.current += 1;
      setState({ ...opts, id: idRef.current });
    });
  }, []);

  const close = useCallback((value: boolean) => {
    const resolver = resolverRef.current;
    resolverRef.current = null;
    setState(null);
    if (resolver) resolver(value);
  }, []);

  const value = useMemo(() => confirm, [confirm]);

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      {state &&
        createPortal(
          <FocusTrap
            className="altiv-modal-shell"
            role="dialog"
            aria-modal="true"
            style={_MODAL_STYLE_Z_TOP}
            onClick={(e: React.MouseEvent) => {
              if (e.target === e.currentTarget) close(false);
            }}
          >
            <div className="altiv-modal-card sm:max-w-md">
              <div className="altiv-modal-header p-6 border-b border-slate-800">
                <div className="flex items-center gap-3">
                  <div
                    className={
                      state.tone === "danger"
                        ? "w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center"
                        : "w-10 h-10 bg-[#0052FF]/20 rounded-full flex items-center justify-center"
                    }
                  >
                    <AlertTriangle
                      className={
                        state.tone === "danger" ? "w-5 h-5 text-red-400" : "w-5 h-5 text-[#4d8cff]"
                      }
                    />
                  </div>
                  <div>
                    <h2
                      className={
                        state.tone === "danger"
                          ? "text-xl font-semibold text-red-400"
                          : "text-xl font-semibold text-white"
                      }
                    >
                      {state.title || t("confirm.defaultTitle")}
                    </h2>
                  </div>
                </div>
              </div>

              <div className="altiv-modal-body p-6">
                <p className="text-slate-300 mb-6 whitespace-pre-line">{state.message}</p>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => close(false)}
                    className="flex-1 px-4 py-3 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-800 transition-colors"
                  >
                    {state.cancelLabel || t("common.cancel")}
                  </button>
                  <button
                    type="button"
                    onClick={() => close(true)}
                    className={
                      state.tone === "danger"
                        ? "flex-1 bg-red-500 hover:bg-red-600 text-white px-4 py-3 rounded-lg transition-colors"
                        : "flex-1 bg-[#0052FF] hover:bg-[#0047E0] text-white px-4 py-3 rounded-lg transition-colors"
                    }
                  >
                    {state.confirmLabel || t("common.confirm")}
                  </button>
                </div>
              </div>
            </div>
          </FocusTrap>,
          document.body
        )}
    </ConfirmContext.Provider>
  );
}
